import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Calendar as CalIcon, Clock, X } from 'lucide-react';
import { cn } from '../../utils/cn';
import { Portal } from '../../utils/Portal';
import { useFloatingPosition } from '../../utils/useFloatingPosition';
import { Calendar } from '../Calendar/Calendar';
import { useFormField } from '../FormField/FormField';

/**
 * DatePicker — sélecteur de date (calendrier) avec presets et heure optionnelle.
 *
 *   <DatePicker
 *     value={date}
 *     onChange={setDate}
 *     withTime
 *     presets={[
 *       { label: "Aujourd'hui", value: () => new Date() },
 *       { label: "Demain 9h", value: () => { const d = new Date(); d.setDate(d.getDate()+1); d.setHours(9,0,0,0); return d; } },
 *     ]}
 *     minDate={new Date()}
 *   />
 *
 * Props :
 *   - value           : Date | null
 *   - onChange        : (Date | null) => void
 *   - withTime        : boolean — ajoute un input HH:MM sous le calendrier
 *   - presets         : array de { label, value: Date | () => Date }
 *   - minDate, maxDate
 *   - size            : 'sm' | 'md' | 'lg'
 *   - placeholder
 *   - isClearable     : boolean (default true)
 *   - isDisabled, isInvalid
 */

const SIZES = {
  sm: 'h-8  text-sm   px-2.5',
  md: 'h-10 text-sm   px-3',
  lg: 'h-11 text-base px-3.5',
};

const DATE_FMT = new Intl.DateTimeFormat('fr-FR', {
  weekday: 'short',
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

const DATETIME_FMT = new Intl.DateTimeFormat('fr-FR', {
  weekday: 'short',
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

function pad(n) { return String(n).padStart(2, '0'); }
function toTimeString(d) { return d ? `${pad(d.getHours())}:${pad(d.getMinutes())}` : ''; }

function applyTime(date, hours, minutes) {
  const d = new Date(date);
  d.setHours(hours, minutes, 0, 0);
  return d;
}

export function DatePicker({
  value,
  onChange,
  withTime = false,
  presets = [],
  minDate,
  maxDate,
  size = 'md',
  placeholder = withTime ? 'Choisir une date et une heure…' : 'Choisir une date…',
  isClearable = true,
  isDisabled,
  isInvalid,
  className,
  id: idProp,
}) {
  const field = useFormField();
  const id = idProp || field?.id;
  const invalid = isInvalid || field?.invalid;

  const [open, setOpen] = useState(false);
  const [visibleMonth, setVisibleMonth] = useState(() => value || new Date());

  const triggerRef = useRef(null);
  const floatingRef = useRef(null);

  const { style } = useFloatingPosition({
    triggerRef,
    floatingRef,
    open,
    side: 'bottom',
    align: 'start',
    offset: 6,
  });

  useEffect(() => {
    if (!open) return;
    setVisibleMonth(value || new Date());
    const onDown = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (floatingRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onEsc);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const displayValue = useMemo(() => {
    if (!value) return '';
    return withTime ? DATETIME_FMT.format(value) : DATE_FMT.format(value);
  }, [value, withTime]);

  const handleSelectDate = useCallback((d) => {
    if (!d) return;
    let next = d;
    // Préserve l'heure courante si withTime + on a déjà une valeur
    if (withTime && value) {
      next = applyTime(d, value.getHours(), value.getMinutes());
    } else if (withTime) {
      // Default 9h
      next = applyTime(d, 9, 0);
    }
    onChange?.(next);
    if (!withTime) setOpen(false);
  }, [onChange, value, withTime]);

  const handleTimeChange = (e) => {
    const v = e.target.value;
    if (!v) return;
    const [hh, mm] = v.split(':').map((s) => parseInt(s, 10));
    if (isNaN(hh) || isNaN(mm)) return;
    const base = value || new Date();
    onChange?.(applyTime(base, hh, mm));
  };

  const handlePreset = (preset) => {
    const v = typeof preset.value === 'function' ? preset.value() : preset.value;
    onChange?.(v);
    setVisibleMonth(v);
    if (!withTime) setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange?.(null);
  };

  return (
    <div className={cn('relative inline-block w-full', className)}>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        onClick={() => !isDisabled && setOpen((o) => !o)}
        disabled={isDisabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-invalid={invalid || undefined}
        aria-describedby={field?.describedBy}
        className={cn(
          'w-full inline-flex items-center justify-between bg-surface text-text outline-none',
          'border border-border rounded-md transition-colors duration-fast',
          'focus:bg-surface focus:border-ink focus:ring-2 focus:ring-ink/15',
          'disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-bg-2',
          SIZES[size],
          invalid && 'border-danger focus:border-danger focus:ring-danger/20',
        )}
      >
        <span className="inline-flex items-center gap-2 min-w-0">
          <CalIcon className="w-4 h-4 text-mute shrink-0" strokeWidth={1.8} />
          <span className={cn('truncate', value ? 'text-text' : 'text-mute-2')}>
            {value ? displayValue : placeholder}
          </span>
        </span>
        {isClearable && value && !isDisabled && (
          <span
            role="button"
            tabIndex={0}
            onClick={handleClear}
            className="text-mute hover:text-text p-0.5 ml-2"
            aria-label="Effacer"
          >
            <X className="w-3.5 h-3.5" strokeWidth={2} />
          </span>
        )}
      </button>

      {open && (
        <Portal>
          <div
            ref={floatingRef}
            data-ds-theme="default"
            role="dialog"
            style={style}
            className="z-overlay bg-surface border border-border rounded-lg shadow-md animate-scale-in origin-top-left p-3 flex gap-3"
          >
            {presets.length > 0 && (
              <div className="w-36 shrink-0 border-r border-border pr-3 -my-1 py-1 overflow-y-auto max-h-[320px]">
                <div className="text-[10.5px] font-mono uppercase tracking-wider text-mute mb-1.5 px-1">
                  Raccourcis
                </div>
                <ul className="flex flex-col gap-0.5">
                  {presets.map((p) => (
                    <li key={p.label}>
                      <button
                        type="button"
                        onClick={() => handlePreset(p)}
                        className="w-full text-left px-2 py-1.5 rounded text-[12.5px] text-text hover:bg-bg-2 transition-colors"
                      >
                        {p.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="min-w-[260px]">
              <Calendar
                mode="single"
                selected={value}
                onSelect={handleSelectDate}
                month={visibleMonth}
                onMonthChange={setVisibleMonth}
                minDate={minDate}
                maxDate={maxDate}
              />
              {withTime && (
                <div className="mt-3 pt-3 border-t border-border flex items-center gap-2">
                  <Clock className="w-3.5 h-3.5 text-mute shrink-0" strokeWidth={1.8} />
                  <label className="text-[12px] font-mono uppercase tracking-wider text-mute" htmlFor={`${id}-time`}>
                    Heure
                  </label>
                  <input
                    id={`${id}-time`}
                    type="time"
                    value={toTimeString(value)}
                    onChange={handleTimeChange}
                    className="ml-auto h-8 px-2 bg-bg-2 border border-border rounded text-[12.5px] font-mono tabular-nums outline-none focus:border-ink focus:ring-2 focus:ring-ink/15"
                  />
                </div>
              )}
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
}

export default DatePicker;
